import React, { forwardRef, useCallback } from 'react';

import {
  focusStoreLocatorFirstRestaurantResult,
  requestStoreLocatorFirstRestaurantFocus,
} from '@fhs-legacy/frontend/src/pages/store-locator/store-locator-focus-handoff';
import {
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from '@fhs-legacy/frontend/src/state/crm-events';
import { useGeolocation } from '@fhs-legacy/frontend/src/state/geolocation';
import {
  STORE_LOCATOR_SEARCH_INPUT,
  STORE_LOCATOR_SUGGESTION_AT,
} from '@fhs-legacy/frontend/src/utils/test-ids';

import { AddressSearchInput } from './address-search-input';
import { LocationPrediction, LocationSearchBarHandle, LocationSearchBarProps } from './types';

/**
 * Store locator address field: a text input backed by Google Places predictions.
 *
 * Selecting a prediction is what moves the map — it writes the manual coordinates on the shared
 * geolocation state, which the locator reads to refetch restaurants.
 */
export const LocationSearchBar = forwardRef<LocationSearchBarHandle, LocationSearchBarProps>(
  function LocationSearchBar(
    { placeholder = 'Enter a location', onSelectLocation, onLocationFound, ...inputProps },
    forwardedRef
  ) {
    const { logRBIEvent } = useCRMEventsContext();
    const { manualAddress, setManualAddress, updateManualCoordinates } = useGeolocation();

    const onSelectPrediction = useCallback(
      (prediction: LocationPrediction, searchText: string) => {
        const address = prediction.description;

        onSelectLocation?.();
        updateManualCoordinates(address, prediction.placeId).then(coordinates => {
          if (coordinates) {
            onLocationFound?.();
          }
        });
        logRBIEvent({
          name: CustomEventNames.MANUAL_LOCATION_SEARCH,
          type: EventTypes.Search,
          attributes: { searchInput: searchText, selectedAddress: address },
        });
        // Tab out of the field should land on the store the search produced.
        requestStoreLocatorFirstRestaurantFocus();
      },
      [logRBIEvent, onLocationFound, onSelectLocation, updateManualCoordinates]
    );

    const onClear = useCallback(() => setManualAddress(''), [setManualAddress]);

    return (
      <AddressSearchInput
        ref={forwardedRef}
        {...inputProps}
        value={manualAddress}
        placeholder={placeholder}
        testID={STORE_LOCATOR_SEARCH_INPUT}
        clearTestID="store-locator-search-clear"
        getPredictionTestID={STORE_LOCATOR_SUGGESTION_AT}
        clearActionName="store locator clear search"
        predictionActionName="store locator select suggestion"
        onSelectPrediction={onSelectPrediction}
        onClear={onClear}
        // Tab out of a field holding a chosen address jumps to the first result rather than
        // walking the whole map control cluster.
        onTabOut={focusStoreLocatorFirstRestaurantResult}
      />
    );
  }
);
